/**
 * MyBadgesPage — the logged-in (non-community-shell) "My Badges" page.
 *
 * Lists the signed-in user's earned recognition badges plus the full badge
 * catalog with an earned flag. Works for EVERY authenticated user (volunteers
 * and regular users alike): the backing APIs (`GET /community/recognition/my`
 * and `/badges`) are user-id-keyed, NOT community-scoped, so no community
 * membership/context is required. This is the destination of the "Badges"
 * sidebar item (nav.badges).
 *
 * Mirrors CommunityRecognitionPage but drops the community wall (which needs a
 * community context) and wraps in GovLayout instead of the community shell.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Award } from 'lucide-react'
import { GovLayout, GovPageTitle, GovEmptyState } from '@/components/layout/GovLayout'
import { BadgeCard, SectionLoading } from '@/components/community/ui'
import { listBadges, myBadges, type BadgeResponse, type MemberBadgeResponse } from '@/services/communityApi'

export default function MyBadgesPage() {
  const { t } = useTranslation()
  const [badges, setBadges] = useState<BadgeResponse[]>([])
  const [mine, setMine] = useState<MemberBadgeResponse[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.allSettled([listBadges(), myBadges()]).then(([b, m]) => {
      if (!active) return
      if (b.status === 'fulfilled') setBadges(b.value)
      if (m.status === 'fulfilled') setMine(m.value)
      setLoading(false)
    })
    return () => { active = false }
  }, [])

  const earnedKeys = new Set(mine.map((mb) => mb.badge.key))

  return (
    <GovLayout>
      <GovPageTitle title={t('nav.badges', 'Badges')} subtitle={t('community.recognition.subtitle')} icon={Award} />

      {/* My earned badges */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('community.recognition.myBadges')}</h2>
      {loading ? <SectionLoading rows={2} /> : mine.length === 0 ? (
        <GovEmptyState icon="🏅" title={t('community.recognition.noBadges')} description={t('community.recognition.noBadgesHint')} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {mine.map((mb) => <BadgeCard key={mb.id} badge={mb.badge} earned />)}
        </div>
      )}

      {/* Full catalog with earned flag */}
      <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('community.recognition.catalog')}</h2>
      {loading ? <SectionLoading rows={3} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {badges.map((b) => <BadgeCard key={b.id} badge={b} earned={earnedKeys.has(b.key)} />)}
        </div>
      )}
    </GovLayout>
  )
}